import { Sparkles, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { useCancelRun } from "@/hooks/use-sources";

interface ProcessSourceProgressDialogProps {
  open: boolean;
  sourceName: string;
  sourceByteLength: number;
  /** Set by useProcessSource's onRunStarted callback once the run is created. */
  runId: string | null;
  /** Called when the user clicks Cancel. Parent should close the dialog. */
  onCancel: () => void;
}

const MIN_ETA_SECONDS = 30;
const MAX_ETA_SECONDS = 300;
const SECONDS_PER_KB = 3;

export function ProcessSourceProgressDialog({
  open,
  sourceName,
  sourceByteLength,
  runId,
  onCancel,
}: ProcessSourceProgressDialogProps) {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const cancel = useCancelRun();

  // Reset the elapsed counter every time the dialog opens.
  useEffect(() => {
    if (!open) {
      setElapsedSeconds(0);
      return;
    }
    setElapsedSeconds(0);
    const id = setInterval(() => setElapsedSeconds((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [open]);

  const estimatedSeconds = estimateSeconds(sourceByteLength);
  const canCancel = runId !== null && !cancel.isPending;

  function handleCancel() {
    if (!runId) return;
    cancel.mutate(runId);
    onCancel();
  }

  return (
    <Dialog open={open}>
      <DialogContent
        className="sm:max-w-md [&>button[data-slot=dialog-close]]:hidden"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <div className="flex items-center gap-2 text-nymbl-navy">
          <Sparkles className="h-5 w-5" />
          <DialogTitle className="font-display text-base font-semibold">
            Processing source
          </DialogTitle>
        </div>
        <DialogDescription className="text-sm text-foreground/80 truncate">
          {sourceName}
        </DialogDescription>

        <div className="mt-2">
          <IndeterminateBar />
        </div>

        <div className="flex items-baseline justify-between text-sm">
          <span className="tabular-nums">
            Elapsed{" "}
            <span className="font-semibold text-foreground">{formatTime(elapsedSeconds)}</span>
          </span>
          <span className="text-muted-foreground tabular-nums">
            ~{formatTime(estimatedSeconds)} estimated
          </span>
        </div>

        <p className="text-xs leading-relaxed text-muted-foreground">
          Extracting scope items from this source. AI extraction usually takes 30 seconds to 3
          minutes depending on source size — feel free to leave this open or work elsewhere.
        </p>

        <div className="flex justify-end pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCancel}
            disabled={!canCancel}
            title={!runId ? "Cancel is available once processing has started" : undefined}
          >
            <X className="h-3.5 w-3.5 mr-1.5" />
            {cancel.isPending ? "Cancelling…" : "Cancel"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function IndeterminateBar() {
  return (
    <div
      role="progressbar"
      aria-label="Processing"
      className="relative h-1.5 w-full overflow-hidden rounded-full bg-muted"
    >
      <div
        className="absolute inset-y-0 w-1/3 rounded-full bg-nymbl-blue"
        style={{
          animation: "nymbl-indeterminate 1.4s ease-in-out infinite",
        }}
      />
      <style>{`
        @keyframes nymbl-indeterminate {
          0%   { left: -34%; }
          50%  { left: 50%; }
          100% { left: 100%; }
        }
      `}</style>
    </div>
  );
}

function estimateSeconds(byteLength: number): number {
  const kb = byteLength / 1024;
  const raw = Math.round(kb * SECONDS_PER_KB);
  return Math.max(MIN_ETA_SECONDS, Math.min(MAX_ETA_SECONDS, raw));
}

function formatTime(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}
